import { AgentAvailability, Agents } from "types/Models/agents";
import { emptyApi } from "./emptyApi";
import { AgentItemType, TodoListTab } from "constants/agentDashboard";
import QueryString from "qs";
import {
  AgentActionItemType,
  ChaserBatch,
  ChaseDraftResponse,
  SnoozedChaseGroup,
} from "types/Models/agentDashboard";

export type AssignableAgent = Agents & { uuid: string };

export const agentsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAssignAgents: build.query<
      AssignableAgent[],
      { agentType?: "ACCOUNT_EXECUTIVE" | "SALES_EXECUTIVE" | "CALL_EXECUTIVE" }
    >({
      query: ({ agentType }) => {
        return {
          url: `/api/inkle/company/agents/`,
          params: { agent_type: agentType },
        };
      },
    }),

    getAgentAvailability: build.query<AgentAvailability[], void>({
      query: () => `/api/inkle/company/v2/support-agent/`,
      providesTags: ["AGENT_AVAILABILITY"],
    }),

    checkAgentsAvailability: build.query<
      {
        available_agent_count: number;
        available_agents: {
          available_end_time?: string;
          available_start_time?: string;
          is_available?: boolean;
          name?: string;
          profile_url?: string;
          type?: "ACCOUNT_EXECUTIVE" | "SALES_EXECUTIVE" | "CALL_EXECUTIVE";
          uuid?: string;
        }[];
      },
      { groupId: string }
    >({
      query: ({ groupId }) =>
        `/api/inkle/company/v2/group/${groupId}/agent-available/`,
      providesTags: ["AGENT_AVAILABILITY"],
    }),

    updateAgentAvailability: build.mutation<
      AgentAvailability,
      {
        agentId: string;
        payload: {
          is_available?: boolean;
          available_start_time?: string;
          available_end_time?: string;
        };
      }
    >({
      query: ({ agentId, payload }) => {
        return {
          url: `/api/inkle/company/v2/support-agent/${agentId}/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["AGENT_AVAILABILITY", "GROUP_SPOCS_AVAILABILITY"],
    }),

    addAgentAvailability: build.mutation<
      AgentAvailability,
      {
        payload: {
          profile_id: string;
          is_available: boolean;
          available_start_time: string;
          available_end_time: string;
        };
      }
    >({
      query: ({ payload }) => {
        return {
          url: `/api/inkle/company/v2/support-agent/`,
          method: "post",
          body: payload,
        };
      },
      invalidatesTags: ["AGENT_AVAILABILITY", "GROUP_SPOCS_AVAILABILITY"],
    }),

    assignAgentTocompany: build.mutation<
      Agents & { group_id: string },
      { userId: string; groupId: string }
    >({
      query: ({ groupId, userId }) => {
        return {
          url: `api/inkle/company/agents/`,
          method: "post",
          body: {
            user_id: userId,
            group_id: groupId,
          },
        };
      },
      invalidatesTags: (result, _error) => {
        return result
          ? [{ type: "CrmCompanyGroups", id: result.group_id }]
          : [{ type: "CrmCompanyGroups", id: "GROUP" }];
      },
    }),

    removeAgentFromCompany: build.mutation<
      {
        group_id: string;
      },
      {
        agentId: string;
        groupId: string;
      }
    >({
      query: ({ agentId, groupId }) => {
        return {
          url: `api/inkle/company/agents/`,
          method: "DELETE",
          body: {
            agent_id: agentId,
            group_id: groupId,
          },
        };
      },
      invalidatesTags: (result, _error) => {
        return result
          ? [{ type: "CrmCompanyGroups", id: result.group_id }]
          : [{ type: "CrmCompanyGroups", id: "GROUP" }];
      },
    }),
    getAgentDashboardDetails: build.query<
      AgentActionItemType[],
      {
        actionType: AgentItemType;
        etdStatusFilter?: TodoListTab;
        agentUserIds?: string | null;
      }
    >({
      query: ({ actionType, etdStatusFilter, agentUserIds }) => {
        const queryUrl = QueryString.stringify(
          {
            action_type: actionType,
            etd_status_filter: etdStatusFilter,
            agent_user_ids: agentUserIds,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/dashboards/v1/admin-dashboard/${queryUrl}`,
        };
      },
      providesTags: ["AGENT_ACTIONS"],
    }),
    getChaseDraft: build.mutation<
      ChaseDraftResponse,
      {
        company_group_id: string;
        items?: Array<{ metric_type: string; item_uuid: string }>;
      }
    >({
      query: (body) => ({
        url: `/api/inkle/dashboards/v1/chase/draft/`,
        method: "POST",
        body,
      }),
    }),

    chaseDraft: build.query<
      ChaseDraftResponse,
      {
        company_group_id: string;
        items?: Array<{ metric_type: string; item_uuid: string }>;
      }
    >({
      query: (body) => ({
        url: `/api/inkle/dashboards/v1/chase/draft/`,
        method: "POST",
        body,
      }),
    }),

    sendChase: build.mutation<
      { success: boolean },
      {
        company_group_id: string;
        message: string;
        items: Array<{ metric_type: string; item_uuid: string }>;
        channel_url?: string;
      }
    >({
      query: (body) => ({
        url: `/api/inkle/dashboards/v1/chase/send/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AGENT_ACTIONS"],
    }),

    snoozeChaseItem: build.mutation<
      { success: boolean },
      {
        company_group_id: string;
        metric_type: string;
        item_uuid: string;
        snooze_days: number;
      }
    >({
      query: (body) => ({
        url: `/api/inkle/dashboards/v1/chase/snooze/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AGENT_ACTIONS", "CHASER_BATCHES"],
    }),

    dismissChaseItem: build.mutation<
      { success: boolean },
      {
        company_group_id: string;
        metric_type: string;
        item_uuid: string;
      }
    >({
      query: (body) => ({
        url: `/api/inkle/dashboards/v1/chase/dismiss/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AGENT_ACTIONS", "CHASER_BATCHES"],
    }),

    markChaseItemActedUpon: build.mutation<
      { success: boolean },
      {
        company_group_id: string;
        metric_type: string;
        item_uuid: string;
      }
    >({
      query: (body) => ({
        url: `/api/inkle/dashboards/v1/chase/mark-acted/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AGENT_ACTIONS", "CHASER_BATCHES"],
    }),

    getChaserBatches: build.query<
      ChaserBatch[],
      { agentUserIds?: string } | void
    >({
      query: (args) => {
        const agentUserIds =
          args && "agentUserIds" in args ? args.agentUserIds : undefined;
        if (agentUserIds) {
          return `/api/inkle/dashboards/v1/chase/batches/?agent_user_ids=${agentUserIds}`;
        }
        return `/api/inkle/dashboards/v1/chase/batches/`;
      },
      providesTags: ["CHASER_BATCHES"],
    }),

    updateChaserBatchMessage: build.mutation<
      ChaserBatch,
      { batchId: string; message_text: string }
    >({
      query: ({ batchId, message_text }) => ({
        url: `/api/inkle/dashboards/v1/chase/batches/${batchId}/`,
        method: "PATCH",
        body: { message_text },
      }),
      invalidatesTags: ["CHASER_BATCHES"],
    }),

    getSnoozedChaseItems: build.query<
      SnoozedChaseGroup[],
      { agentUserIds?: string } | void
    >({
      query: (args) => {
        const agentUserIds =
          args && "agentUserIds" in args ? args.agentUserIds : undefined;
        if (agentUserIds) {
          return `/api/inkle/dashboards/v1/chase/snoozed/?agent_user_ids=${agentUserIds}`;
        }
        return `/api/inkle/dashboards/v1/chase/snoozed/`;
      },
      providesTags: ["CHASER_BATCHES"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAssignAgentTocompanyMutation,
  useGetAssignAgentsQuery,
  useRemoveAgentFromCompanyMutation,
  useGetAgentAvailabilityQuery,
  useUpdateAgentAvailabilityMutation,
  useAddAgentAvailabilityMutation,
  useCheckAgentsAvailabilityQuery,
  useGetAgentDashboardDetailsQuery,
  useGetChaseDraftMutation,
  useChaseDraftQuery,
  useSendChaseMutation,
  useSnoozeChaseItemMutation,
  useDismissChaseItemMutation,
  useMarkChaseItemActedUponMutation,
  useGetChaserBatchesQuery,
  useUpdateChaserBatchMessageMutation,
  useGetSnoozedChaseItemsQuery,
} = agentsApi;
